import React, { useEffect, useRef, useState } from "react";
import {
  IonPage,
  IonContent,
  IonButton,
  IonText,
  IonSpinner,
} from "@ionic/react";
import { useDispatch, useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { fetchPreLoginSlides } from "../../redux/actions/preLoginActions";
import { trackFunnel } from "../../services/funnelTracker";
import { readEmailAttempt } from "../../services/emailSignIn";
import { LOGIN_PATH, REGISTRATION_PATH } from "../../services/signInSession";
import "./PreLogin.min.css";

// Import Swiper styles
import "swiper/css";
import { useHistory } from "react-router";

/**
 * The button under each intro screen (client, 14/09/2026): the first explains,
 * the middle ones continue, the last one starts the registration.
 */
const ctaLabelFor = (index, total) => {
  if (index >= total - 1) return "INIZIA GRATIS";
  if (index === 0) return "SCOPRI COME FUNZIONA";
  return "CONTINUA";
};

const PreLogin = () => {
  const dispatch = useDispatch();
  const swiperRef = useRef(null);
  const viewedSlidesRef = useRef(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const [imagesReady, setImagesReady] = useState(false);

  const { slides, loading } = useSelector((state) => state.preLogin);
  const history = useHistory();

  useEffect(() => {
    // Back from the mail app with a sign-in email on its way: go straight to
    // where its code can be typed instead of showing the intro again.
    const attempt = readEmailAttempt();
    if (attempt) {
      history.replace(attempt.mode === "registration" ? REGISTRATION_PATH : LOGIN_PATH);
      return;
    }
    dispatch(fetchPreLoginSlides());
  }, [dispatch, history]);

  // Preload all slide images before showing the carousel
  useEffect(() => {
    if (!slides || slides.length === 0) return;
    let cancelled = false;

    const preload = slides.map(
      (slide) =>
        new Promise((resolve) => {
          if (!slide.image_url) {
            resolve();
            return;
          }
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve; // don't block on failed images
          img.src = slide.image_url;
        }),
    );

    Promise.all(preload).then(() => {
      if (!cancelled) setImagesReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [slides]);

  const isLastSlide = slides.length > 0 && activeIndex >= slides.length - 1;

  const trackSlideViewed = (index) => {
    if (viewedSlidesRef.current.has(index)) return;
    viewedSlidesRef.current.add(index);
    trackFunnel("intro_slide_viewed", { slide: index + 1 });
  };

  const handleNext = () => {
    if (isLastSlide) {
      history.replace(REGISTRATION_PATH);
    } else {
      swiperRef.current?.slideNext();
    }
  };

  const handleSkip = () => {
    trackFunnel("intro_skipped", { slide: activeIndex + 1 });
    history.replace(REGISTRATION_PATH);
  };

  const handleLogin = () => {
    trackFunnel("intro_login_clicked", { slide: activeIndex + 1 });
    history.replace(LOGIN_PATH);
  };

  if (loading || (slides.length > 0 && !imagesReady)) {
    return (
      <IonPage className="prelogin-page loading-state">
        <IonContent>
          <div className="loader-container">
            <IonSpinner name="crescent" color="primary" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage className="prelogin-page">
      <IonContent fullscreen scrollY={false}>
        {/* Swiper Container */}
        {slides.length > 0 ? (
          <Swiper
            onInit={(swiper) => {
              swiperRef.current = swiper;
              trackSlideViewed(swiper.activeIndex);
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex);
              trackSlideViewed(swiper.activeIndex);
            }}
            className="full-height-swiper"
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={index}>
                <div
                  className="slide-content"
                  style={{ backgroundImage: `url(${slide.image_url})` }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          // Fallback if no slides found
          <div className="no-slides">
            <IonButton onClick={() => history.replace(REGISTRATION_PATH)}>
              Continua
            </IonButton>
          </div>
        )}

        {/* "Salta" on every screen but the last, straight to the registration */}
        {slides.length > 0 && !isLastSlide ? (
          <button type="button" className="prelogin-skip" onClick={handleSkip}>
            Salta
          </button>
        ) : null}

        {/* Fixed Bottom Area */}
        <div className="fixed-bottom-area">
          {slides.length > 0 ? (
            <IonButton
              expand="block"
              className="main-button"
              shape="round"
              onClick={handleNext}
            >
              {ctaLabelFor(activeIndex, slides.length)}
            </IonButton>
          ) : null}

          {/* Under every screen: members who already have an account sign in */}
          <div
            className="bottom-skip-link visible"
            role="button"
            onClick={handleLogin}
          >
            <IonText>
              Hai già un account? <span className="bottom-login-word">Accedi</span>
            </IonText>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PreLogin;
